import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatChip, MatChipSet } from '@angular/material/chips'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatFormField } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatProgressBar } from '@angular/material/progress-bar'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatToolbar } from '@angular/material/toolbar'
import { MatTooltip } from '@angular/material/tooltip'
import { HasRoleDirective } from '@elderbyte/ngx-auth'
import {
  ElderDialogService,
  ElderHeaderComponent,
  ElderSelectComponent,
  ElderTableComponent,
  ElderThemeDirective,
  ElderToastService,
  ErrorUtil,
  ExceptionDetail,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, from, Observable, of } from 'rxjs'
import { catchError, filter, mergeMap, switchMap, tap } from 'rxjs/operators'
import { ProdOrder } from '../../prod-order'
import { ProdOrderService } from '../../prod-order.service'
import {
  ProdOrderCommand,
  ProdOrderCommandResult,
  ProdOrderCommandType,
} from '../prod-order-command'
import { ProdOrderCommandSelectDirective } from '../prod-order-command-select.directive'
import { ProdOrderCommandDialogResponse } from '../ProdOrderCommandDialogResponse'

export interface IProdOrderCommandBatchDialogOptions {
  orders: ProdOrder[]
}

export enum JobState {
  IDLE = 'IDLE',
  RUNNING = 'RUNNING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export class ProdOrderCommandJobStatus {
  public static idle(): ProdOrderCommandJobStatus {
    return new ProdOrderCommandJobStatus(JobState.IDLE)
  }

  public static running(): ProdOrderCommandJobStatus {
    return new ProdOrderCommandJobStatus(JobState.RUNNING)
  }

  public static failed(err: any): ProdOrderCommandJobStatus {
    return new ProdOrderCommandJobStatus(JobState.FAILED, ErrorUtil.buildExceptionDetail(err))
  }

  public static done(result: ProdOrderCommandResult): ProdOrderCommandJobStatus {
    return new ProdOrderCommandJobStatus(result.success ? JobState.SUCCESS : JobState.FAILED)
  }

  constructor(
    public readonly state: JobState,
    public readonly error: ExceptionDetail = null
  ) {}
}

export class ProdOrderCommandJob {
  public readonly request$: Observable<ProdOrderCommandResult>
  public readonly status$ = new BehaviorSubject<ProdOrderCommandJobStatus>(
    ProdOrderCommandJobStatus.idle()
  )

  constructor(
    public order: ProdOrder,
    public cmd: ProdOrderCommand
  ) {}
}

@Component({
  selector: 'app-prod-order-command-batch-dialog',
  templateUrl: './prod-order-command-batch-dialog.component.html',
  styleUrls: ['./prod-order-command-batch-dialog.component.scss'],
  imports: [
    MatToolbar,
    ElderThemeDirective,
    MatIcon,
    ElderHeaderComponent,
    MatIconButton,
    MatFormField,
    ElderSelectComponent,
    ProdOrderCommandSelectDirective,
    ElderTableComponent,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatChipSet,
    MatChip,
    MatProgressBar,
    MatTooltip,
    HasRoleDirective,
    MatButton,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ProdOrderCommandBatchDialogComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly commandType$ = new BehaviorSubject<ProdOrderCommandType>(null)
  public readonly jobs$ = new BehaviorSubject<ProdOrderCommandJob[]>([])
  private dialogRef = inject<MatDialogRef<ProdOrderCommandBatchDialogComponent>>(MatDialogRef)
  private data = inject<IProdOrderCommandBatchDialogOptions>(MAT_DIALOG_DATA)
  private readonly prodOrderService = inject(ProdOrderService)
  private readonly toastService = inject(ElderToastService)
  private dialogService = inject(ElderDialogService)
  public readonly JobState = JobState

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.jobs$.next(this.data.orders.map((o) => new ProdOrderCommandJob(o, null)))

    this.commandType$.pipe(filter((cmd) => !!cmd)).subscribe((action) => {
      this.jobs$.next(this.data.orders.map((o) => this.buildJob(o, action)))
    })
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get commandType(): ProdOrderCommandType {
    return this.commandType$.getValue()
  }

  public set commandType(type: ProdOrderCommandType) {
    if (type) {
      this.commandType$.next(type)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public prepareAndRunCommands(force: boolean): void {
    if (force && this.commandType === ProdOrderCommandType.PREPARE_ITEMS) {
      this.orderCommandConfirmDialog().subscribe((result) => {
        if (result) {
          this.runCommands(force)
        }
      })
    } else {
      this.runCommands(force)
    }
  }

  public close(): void {
    this.closeInternal(ProdOrderCommandDialogResponse.ignore())
  }

  public stringify(obj: any): string {
    return JSON.stringify(obj)
  }

  coerceJob(job: any): ProdOrderCommandJob {
    return <ProdOrderCommandJob>job
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private runCommands(force: boolean): void {
    from(this.jobs$.getValue())
      .pipe(mergeMap((job) => this.buildRequest(job, force), 2))
      .subscribe({
        next: (_) => this.toastService.info('All commands completed.'),
        error: (err) => this.toastService.error('Failed to run commands!', err),
      })
  }

  private buildRequest(
    job: ProdOrderCommandJob,
    force: boolean
  ): Observable<ProdOrderCommandResult> {
    const commandClone = Object.assign({}, job.cmd)
    commandClone.force = force

    const request$ = this.prodOrderService.executeCommand(commandClone)
    return of(null).pipe(
      tap((_) => job.status$.next(ProdOrderCommandJobStatus.running())),
      switchMap((_) =>
        request$.pipe(
          tap((result) => job.status$.next(ProdOrderCommandJobStatus.done(result))),
          catchError((err) => {
            this.log.error('Failed on command job ' + JSON.stringify(commandClone), err)
            job.status$.next(ProdOrderCommandJobStatus.failed(err))
            return of(new ProdOrderCommandResult(job.order, commandClone.type, false))
          })
        )
      )
    )
  }

  private closeInternal(response: ProdOrderCommandDialogResponse): void {
    this.dialogRef.close(response)
  }

  private buildJob(order: ProdOrder, action: ProdOrderCommandType): ProdOrderCommandJob {
    const cmd = new ProdOrderCommand(order.id, action)
    return new ProdOrderCommandJob(order, cmd)
  }

  private orderCommandConfirmDialog(): Observable<boolean> {
    return this.dialogService.showConfirm({
      title: 'Befehl wirklich ausführen?',
      message:
        'Dieser Befehl kann Auswirkungen auf bereits abgeschlossene Positionen haben! ' +
        'Insbesondere Daten, die während der Produktion erstellt wurden könnten verloren gehen!',
      yesNo: true,
    })
  }
}
